/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/explicit-function-return-type */

import {
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnChanges,
  OnInit,
  Optional,
  Self,
  SimpleChanges,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  FormControl,
  NgControl,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { CountryCode } from 'libphonenumber-js';
import { tap } from 'rxjs';

import { ConstantsService } from '~core/services/constants.service';
import { FormFieldModule } from '~ui-kit/form-field';
import { PhoneNumberComponent, PhoneNumberModule } from '~ui-kit/inputs';

@Component({
  selector: 'phone-field',
  templateUrl: './phone-field.component.html',
  styleUrls: ['./phone-field.component.scss'],
  standalone: true,
  imports: [ReactiveFormsModule, PhoneNumberModule, FormFieldModule]
})
export class PhoneFieldComponent
  implements OnInit, OnChanges, ControlValueAccessor
{
  protected _destroyRef = inject(DestroyRef);
  protected _cdRef = inject(ChangeDetectorRef);
  protected _constantsService = inject(ConstantsService);

  uniqueId = input.required<string>();
  label = input<string | null | undefined>(undefined);
  disableLabel = input(false);
  defaultCountry = input<string | null | undefined>(undefined);
  selectedCountry = input<string | null | undefined>(undefined);
  placeholder = input<string | null | undefined>(undefined);
  preferredCountries = input<CountryCode[] | string[] | null | undefined>(
    undefined
  );
  disableDefaultValidation = input(false);
  readonly = input(false);

  textLabel = computed(() => {
    return {
      mainLabel: '',
      codePlaceholder: 'Code',
      searchPlaceholderLabel: 'Rechercher',
      noEntriesFoundLabel: 'Aucun pays trouvé',
      nationalNumberLabel: '',
      hintLabel: '',
      invalidNumberError: 'Numéro invalide',
      requiredError: ''
    };
  });

  phoneControl = new FormControl<string | undefined | null>(null);
  phoneNumberRef = viewChild(PhoneNumberComponent);

  readonly requiredValidator = Validators.required;
  readonly _defaultCountry =
    this._constantsService.defaultCountry.toLowerCase();

  constructor(@Optional() @Self() public ngControl: NgControl) {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit() {
    this.listenToControlChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedCountry && !changes.selectedCountry.firstChange) {
      this.phoneNumberRef()?.selectCountryCode(
        changes.selectedCountry.currentValue,
        false
      );
    }
  }

  listenToControlChanges(): void {
    this.phoneControl.valueChanges
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap({
          next: value => {
            this._onChange(this.phoneControl.valid ? value : null);
          }
        })
      )
      .subscribe();
  }

  handleBlur(): void {
    this._onTouched();
  }

  // Control value Accessor
  private _onChange = (value: string | undefined | null) => undefined;
  registerOnChange(fn: (value: string | undefined | null) => any): void {
    this._onChange = fn;
  }

  private _onTouched = () => undefined;
  registerOnTouched(fn: () => undefined): void {
    this._onTouched = fn;
  }

  writeValue(value: string | undefined | null): void {
    this.phoneControl.setValue(value, { emitEvent: false });
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.phoneControl.disable();
    } else {
      this.phoneControl.enable();
    }
  }

  get isControlRequired(): boolean {
    return this.ngControl?.control?.hasValidator(Validators.required) ?? false;
  }
}
